import { Boxes, Plus, X } from 'lucide-react';
import type { RequisitionLineDto, StoreroomDto } from '@eombor/contracts';
import { Button, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { Select } from '../../../shared/ui/Select.js';

/**
 * QABULNI TAQSIMLASH — har mahsulot, kerak bo'lsa MIQDORI bilan.
 *
 * Ilgari qabulda bitta ombor tanlanardi va kelgan hamma narsa o'sha
 * yerga tushardi. Amalda bitta talabnomada bir necha xil mahsulot
 * keladi va ular boshqa-boshqa joyda saqlanadi — ustiga BITTA
 * mahsulotning o'zi ham bo'linadi: o'nta paneldan oltitasi garajga,
 * to'rttasi seyfga. Xodim hammasini bitta omborga qabul qilib, keyin
 * bittalab ko'chirishga majbur edi — ko'chirish esa BUTUN lotni
 * ko'chirardi, ya'ni miqdorni bo'lishning yo'li umuman yo'q edi.
 *
 * Har qator — bitta mahsulot va uning MANZILLARI. Manzil bitta bo'lsa
 * miqdor so'ralmaydi: hammasi o'sha yerga tushadi va ortiqcha maydon
 * faqat chalg'itardi. Ikkinchisi qo'shilganda miqdor paydo bo'ladi.
 *
 * OXIRGI manzilning miqdori YOZILMAYDI — u qoldiqni oladi. Yig'indini
 * odamga sanatib qo'yish xato manbai: bir donasi yo'qolib, qabul 400
 * bilan qaytardi.
 */

export interface PlacementSpot {
  storeroomId: string;
  zoneId: string | null;
  /** `null` — qolgan hammasi. Oxirgi manzilda doim `null`. */
  qty: number | null;
}

export interface ReceiptPlacementListProps {
  lines: readonly RequisitionLineDto[];
  rooms: readonly StoreroomDto[];
  /** Partiya → manzillar. Kalit `batchId`, server ham shunday kutadi. */
  value: ReadonlyMap<string, PlacementSpot[]>;
  onChange: (batchId: string, spots: PlacementSpot[]) => void;
  nameOf: (nomenclatureId: string) => { name: string; unit: string } | undefined;
}

/** Miqdorni uch xonagacha yaxlitlaydi — ustun `numeric(14,3)`. */
const qtyText = (qty: number): string => String(Math.round(qty * 1000) / 1000);

/**
 * QABULGA KELGANLAR — satr emas, PARTIYA bo'yicha.
 *
 * Bitta talabnoma satri omborda bir nechta katakdan to'lgan bo'lishi
 * mumkin (3 tasi bir partiyadan, 1 tasi boshqasidan) va bo'linmaga ham
 * shuncha lot bo'lib tushadi. Ilgari bu yerda satrlar sanalardi va
 * ikkinchi partiya ekranda umuman ko'rinmasdi — qabul esa uni baribir
 * kutardi va 400 bilan qaytardi.
 */
const arrivals = (
  lines: readonly RequisitionLineDto[],
): { line: RequisitionLineDto; batchId: string; total: number }[] =>
  lines.flatMap((line) =>
    line.placements.map((place) => ({ line, batchId: place.batchId, total: place.qty })),
  );

export const ReceiptPlacementList = ({
  lines,
  rooms,
  value,
  onChange,
  nameOf,
}: ReceiptPlacementListProps) => {
  /* Partiyasi yo'q satr qabulga yetib kelmaydi: partiya berish paytida
     qo'yiladi. */
  const placeable = arrivals(lines);
  if (placeable.length === 0) return null;

  return (
    <ul className="flex flex-col gap-2">
      {placeable.map(({ line, batchId, total }) => {
        const info = nameOf(line.nomenclatureId);
        const spots = value.get(batchId) ?? [];
        const unit = info?.unit ?? '';

        /* Berilgan miqdorlarning yig'indisi — oxirgisidan tashqari.
           Oxirgisi qoldiqni oladi va u shu yerda ko'rsatiladi. */
        const assigned = spots
          .slice(0, -1)
          .reduce((sum, spot) => sum + (spot.qty ?? 0), 0);
        const rest = Math.round((total - assigned) * 1000) / 1000;

        const patch = (index: number, next: Partial<PlacementSpot>): void => {
          onChange(
            batchId,
            spots.map((spot, i) => (i === index ? { ...spot, ...next } : spot)),
          );
        };

        const addSpot = (): void => {
          /* Yangi manzil qo'shilganda OLDINGISI miqdorga ega bo'ladi:
             u endi oxirgi emas va qoldiqni ololmaydi. */
          const first = rooms[0];
          if (first === undefined) return;
          const grown = spots.map((spot, i) =>
            i === spots.length - 1 ? { ...spot, qty: spot.qty ?? rest } : spot,
          );
          onChange(batchId, [...grown, { storeroomId: first.id, zoneId: null, qty: null }]);
        };

        const removeSpot = (index: number): void => {
          const left = spots.filter((_, i) => i !== index);
          /* Oxirgisi qoldiqni oladi — miqdori tozalanadi. */
          onChange(
            batchId,
            left.map((spot, i) => (i === left.length - 1 ? { ...spot, qty: null } : spot)),
          );
        };

        return (
          <li
            /* Kalit SATR EMAS, partiya: bitta satr bir nechta partiyadan
               kelgan bo'lsa, u shuncha qator bo'lib chiziladi. */
            key={`${line.id}:${batchId}`}
            className="flex flex-col gap-2 rounded-[var(--radius-md)] border border-border bg-surface px-3 py-2.5"
          >
            <p className="flex items-center gap-2">
              <Boxes size={15} aria-hidden className="shrink-0 text-text-faint" />
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-text">
                {info?.name ?? line.nomenclatureId}
              </span>
              <span className="tabular shrink-0 text-sm text-text-muted">
                {qtyText(total)} {unit}
              </span>
            </p>

            {spots.map((spot, index) => {
              const room = rooms.find((item) => item.id === spot.storeroomId);
              const last = index === spots.length - 1;
              return (
                <div key={index} className="flex flex-wrap items-end gap-2 pl-6">
                  <Select
                    label={t('storeroom.pickFor', { name: info?.name ?? line.nomenclatureId })}
                    hiddenLabel
                    value={spot.storeroomId}
                    onChange={(event) => patch(index, { storeroomId: event.target.value, zoneId: null })}
                    options={rooms.map((item) => ({ value: item.id, label: item.name }))}
                    className="w-[11rem]"
                  />

                  {room === undefined || room.zones.length === 0 ? null : (
                    <Select
                      label={t('storeroom.zoneFor', { name: info?.name ?? line.nomenclatureId })}
                      hiddenLabel
                      value={spot.zoneId ?? ''}
                      onChange={(event) =>
                        patch(index, { zoneId: event.target.value === '' ? null : event.target.value })
                      }
                      options={[
                        { value: '', label: t('storeroom.noZone') },
                        ...room.zones.map((zone) => ({ value: zone.id, label: zone.name })),
                      ]}
                      className="w-[9.5rem]"
                    />
                  )}

                  {/* Manzil BITTA bo'lsa miqdor so'ralmaydi: hammasi
                      o'sha yerga tushadi. Oxirgisiniki ham so'ralmaydi —
                      u qoldiqni oladi va shunchaki ko'rsatiladi. */}
                  {spots.length === 1 ? null : last ? (
                    <span
                      className={cn(
                        'tabular flex h-[var(--size-control)] items-center rounded-[var(--radius-sm)]',
                        'border border-dashed border-border px-3 text-sm',
                        rest < 0 ? 'text-danger' : 'text-text-muted',
                      )}
                    >
                      {qtyText(rest)} {unit}
                    </span>
                  ) : (
                    <input
                      type="number"
                      inputMode="decimal"
                      min={0}
                      max={total}
                      step="any"
                      aria-label={t('storeroom.qtyFor', { name: info?.name ?? line.nomenclatureId })}
                      value={spot.qty ?? ''}
                      onChange={(event) =>
                        patch(index, {
                          qty: event.target.value === '' ? null : Number(event.target.value),
                        })
                      }
                      className={cn(
                        'h-[var(--size-control)] w-24 rounded-[var(--radius-sm)] border',
                        'border-border-control bg-surface px-3 text-base text-text',
                      )}
                    />
                  )}

                  {spots.length === 1 ? null : (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('storeroom.removeSpot')}
                      onClick={() => removeSpot(index)}
                    >
                      <X size={14} aria-hidden />
                    </Button>
                  )}
                </div>
              );
            })}

            {rooms.length < 2 ? null : (
              <Button variant="ghost" size="sm" className="ml-6 self-start" onClick={addSpot}>
                <Plus size={14} aria-hidden />
                {t('storeroom.addSpot')}
              </Button>
            )}

            {rest < 0 ? (
              <p className="pl-6 text-xs text-danger">{t('storeroom.splitTooBig')}</p>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
};
